'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledState } from '../../hooks/use-disabled-state';
import { ChipGroupContext } from '../chip-group/context/chip-group.context';
import { AssistChipContext } from './context/assist-chip.context';
import { assistChipRootRecipe } from './styles/assist-chip-root.styles';
import type { AssistChipRootPropsInterface } from './types/assist-chip-root.types';

/**
 * @since 10.0.0
 */
export const AssistChipRoot = forwardRef<HTMLButtonElement, AssistChipRootPropsInterface>(
  (props: AssistChipRootPropsInterface, forwardedRef) => {
    const chipdGroupContext = useContext(ChipGroupContext);

    const {
      children,
      elevated = chipdGroupContext.elevated || false,
      className,
      css: cssProp,
      disabled = chipdGroupContext.disabled || false,
      loading = false,
      loadingVoiceOver = 'Loading',
      ...rest
    } = props;

    const isDisabled = disabled || loading;

    const styles = css(assistChipRootRecipe.raw({ elevated, disabled, loading }), cssProp);

    const a11yDisabledState = useDisabledState({ disabled: isDisabled, focusable: true });

    const contextValue = useMemo(
      () => ({ disabled, loading, loadingVoiceOver }),
      [disabled, loading, loadingVoiceOver],
    );

    return (
      <AssistChipContext.Provider value={contextValue}>
        <button
          ref={forwardedRef}
          className={cx('c11n', styles, className)}
          data-c11n-component="AssistChip.Root"
          data-c11n-version={__C11N_VERSION__}
          type="button"
          {...rest}
          {...a11yDisabledState}
        >
          {children}
        </button>
      </AssistChipContext.Provider>
    );
  },
);

AssistChipRoot.displayName = 'AssistChip.Root';
